import React, { useCallback } from 'react'
import { draftQuestionnaireRefetchQuery } from '../../../data/gql/questionnaire/queries'
import { fieldworkRefetchQuery } from '../../../data/gql/questionnaire/queries/fieldwork'
import { getFeasibilityEstimateRefetchQuery } from '../../../data/gql/questionnaire/queries/getFeasibilityEstimate'
import { useUpdateFieldworkV2 } from '../../../hooks/audience/createOrUpdateFieldworkV2'
import { useFieldwork } from '../../../hooks/useFieldwork'
import { useIsAudienceReadOnly } from '../../../hooks/useIsAudienceReadOnly'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../hooks/useProjectId'
import { useSurveyId } from '../../../hooks/useSurveyId'
import { checkIsResultsPage } from '../../../utils/HelperFunctions'
import { convertAudienceType } from '../../../utils/audienceHelper'
import CompletesInputControl from './CompletesInput.control'
import CompletesInputLoading from './CompletesInputLoading'

interface Props {
  inputLabel?: string
  fullWidth?: boolean
}

const CompletesInput: React.FC<Props> = (props: Props) => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()
  const { inputLabel } = props
  const { isReadOnly } = useIsAudienceReadOnly()

  const { fieldwork, loading: loadingFieldwork } = useFieldwork({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first'
  })

  // samplesNeeded is the single source of truth for the total sample size.
  // Per-language splits are stored as percentages on languageAllocations and
  // derived as samplesNeeded * percent on the backend — so the top input is
  // always the authoritative total and stays directly editable (the old
  // "lock when allocations exist / show the sum" behaviour from the
  // absolute-samples model no longer applies).
  const defaultCompletes = fieldwork?.samplesNeeded.unadjusted
  const loi = fieldwork?.lengthOfInterview.adjusted || 2

  const { updateFieldworkV2 } = useUpdateFieldworkV2()

  const handleSaveCompletes = useCallback(
    (completes: number | undefined) => {
      if (
        completes !== undefined &&
        completes !== defaultCompletes &&
        completes > 0 &&
        fieldwork?.audience
      ) {
        const panelSupplierCode = convertAudienceType(
          fieldwork.audience[0].__typename
        )

        updateFieldworkV2({
          variables: {
            input: {
              surveyId,
              panelSupplierCode: panelSupplierCode,
              lengthOfInterview: null,
              samplesNeeded: completes,
              startDate: null,
              endDate: null,
              countryCode: null
            }
          },
          refetchQueries: [
            fieldworkRefetchQuery(surveyId),
            draftQuestionnaireRefetchQuery(projectId, surveyId, waveId),
            getFeasibilityEstimateRefetchQuery(projectId, surveyId, loi)
          ]
        })
      }
    },
    [
      defaultCompletes,
      loi,
      fieldwork?.audience,
      projectId,
      surveyId,
      updateFieldworkV2,
      waveId
    ]
  )

  if (loadingFieldwork) {
    return <CompletesInputLoading />
  }

  const isInputDisabled = checkIsResultsPage() || isReadOnly

  return (
    <CompletesInputControl
      disabled={isInputDisabled}
      defaultCompletes={defaultCompletes}
      inputLabel={inputLabel}
      saveCompletes={handleSaveCompletes}
    />
  )
}

export default CompletesInput
